// NotificationItem — карточка одного уведомления.
// Используется в Panel (desktop) и Sheet (mobile) и в списке /notifications.
//
// Тактильный редизайн «Картотека» (§3.6, §8.V): строка — поверхность e1,
// на hover приподнимается (e3 + translateY), на :active — лёгкое вдавливание.
// Непрочитанное — акцент-маркер: вертикальная полоса слева + точка справа +
// уплотнённый фон. Иконка типа — CategoryTile (категорийная подложка §3.3).

import type * as Shared from "@studyhub/shared";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@studyhub/ui/components/dropdown-menu";
import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { SwipeActions } from "@studyhub/ui/components/swipe-actions";
import { IconCheck, IconChevronRight, IconDotsVertical, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

type NotificationType = Shared.notifications.NotificationType;

import { FormattedRelativeTime } from "@/components/i18n/formatters";
import { useOpenNotification } from "@/hooks/use-open-notification";
import { cn } from "@/lib/cn";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { resolveNotificationVisual } from "./notification-visuals";

export interface NotificationItemData {
	id: string;
	type: NotificationType | string;
	title: string;
	message: string;
	createdAt: string;
	read: boolean;
	actionUrl?: string | null;
	/** Тип артефакта (`data.kind`) — для выбора иконки раздела. */
	kind?: string | null;
	/**
	 * Ключ схлопывания однотипных уведомлений (`material:<id>`,
	 * `recording-audio:<id>`, …). Сервер писал его всегда, но адаптер до
	 * 2026-08-14 выбрасывал — из-за чего «3 артефакта по одному материалу»
	 * показывались тремя строками. См. `aggregate-notifications`.
	 */
	groupKey?: string | null;
}

interface NotificationItemProps {
	item: NotificationItemData;
	onMarkRead?: (id: string) => void;
	onDelete?: (id: string) => void;
	/** Вызывается при переходе по уведомлению — на mobile закрывает Sheet,
	 *  чтобы пользователь увидел открывшуюся страницу, а не пустую панель. */
	onSelect?: () => void;
	compact?: boolean;
}

export function NotificationItem({
	item,
	onMarkRead,
	onDelete,
	onSelect,
	compact,
}: NotificationItemProps) {
	const { t } = useTranslation("notifications");
	const { category, Icon } = resolveNotificationVisual({ type: item.type, kind: item.kind });
	const openNotification = useOpenNotification();

	// Клик ведёт на связанную сущность (actionUrl) или на деталь уведомления.
	// onSelect закрывает mobile-sheet, чтобы пользователь увидел открывшийся экран.
	const handleOpen = () => {
		onSelect?.();
		openNotification(item);
	};

	const inner = (
		<div
			className={cn(
				// Тактильная строка: e1 в покое, e3 + подъём на hover, вдавливание на active.
				"group/notif relative flex items-start gap-3 overflow-hidden rounded-[var(--radius-card)]",
				"border border-border shadow-e1",
				"transition-[transform,box-shadow,background-color] duration-fast",
				"ease-[var(--ease-spring)]",
				"hover:-translate-y-[2px] hover:shadow-e3",
				"active:translate-y-0 active:shadow-e1",
				compact ? "p-2.5 pl-3.5" : "p-3 pl-4",
				// Панель bg-card: непрочитанное чуть тонируем, прочитанное — well.
				item.read ? "bg-muted/40" : "bg-muted/20",
			)}
		>
			{/* Акцент-маркер непрочитанного — вертикальная полоса слева. */}
			{!item.read && (
				<span aria-hidden className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-primary" />
			)}

			<CategoryTile
				category={category}
				icon={<Icon stroke={1.75} />}
				size={compact ? "sm" : "md"}
			/>

			<div className="min-w-0 flex-1">
				<div className="flex min-w-0 items-start gap-2">
					<span
						className={cn(
							"min-w-0 flex-1 truncate text-sm leading-snug",
							item.read ? "font-normal text-muted-foreground" : "font-semibold text-foreground",
						)}
					>
						{item.title}
					</span>
					{!item.read && (
						<span
							aria-hidden
							className="mt-1 size-2 shrink-0 rounded-full bg-primary ring-[3px] ring-primary/15"
						/>
					)}
				</div>
				<p className="mt-0.5 line-clamp-2 break-words text-xs leading-relaxed text-muted-foreground">
					{item.message}
				</p>
				<div className="mt-1.5 flex items-center gap-1.5 text-2xs tabular-nums text-muted-foreground/80">
					<FormattedRelativeTime value={item.createdAt} />
				</div>
			</div>

			{/* Хвост: chevron-аффорданс (мягко появляется на hover) + меню действий.
			    ⋮-меню — ТОЛЬКО desktop: на mobile действия даёт свайп (mark-read/delete),
			    а Radix DropdownMenu внутри vaul BottomSheet открывает портал ВНЕ шторки
			    → vaul принимает это за тап-снаружи и закрывает панель (фидбек). */}
			<div className="flex shrink-0 items-center gap-0.5 self-center">
				{!IS_MOBILE_PLATFORM && (onMarkRead ?? onDelete) && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className={cn(
									"flex cursor-pointer items-center justify-center rounded-[var(--radius-tile)] p-1.5 text-muted-foreground",
									"pointer-coarse:min-h-11 pointer-coarse:min-w-11",
									"transition-[opacity,background-color,color] duration-fast",
									"hover:bg-muted hover:text-foreground",
									// На touch (нет hover) — всегда видно; на desktop — по hover строки.
									"opacity-100 lg:opacity-0 lg:group-hover/notif:opacity-100",
								)}
								aria-label={t("actions.menu")}
								// Только stopPropagation (НЕ preventDefault): Radix-триггер
								// открывается по click через composeEventHandlers — preventDefault
								// ребёнка пропускал бы открытие меню на touch («⋮ не кликается»).
								// stopPropagation гасит навигацию по родительской <a>.
								onClick={(e) => e.stopPropagation()}
							>
								<IconDotsVertical size={14} />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{!item.read && onMarkRead && (
								<DropdownMenuItem onClick={() => onMarkRead(item.id)}>
									<IconCheck size={14} />
									{t("markRead")}
								</DropdownMenuItem>
							)}
							{onDelete && (
								<DropdownMenuItem
									onClick={() => onDelete(item.id)}
									className="text-destructive focus:text-destructive"
								>
									<span style={{ color: "var(--destructive)" }}>
										<IconTrash size={14} />
									</span>
									{t("delete")}
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
				<IconChevronRight
					size={15}
					stroke={1.75}
					aria-hidden
					className={cn(
						"text-muted-foreground/40 transition-[transform,color] duration-fast",
						"group-hover/notif:translate-x-0.5 group-hover/notif:text-muted-foreground",
					)}
				/>
			</div>
		</div>
	);

	// Семантический <a> (а не div role=link): даёт правый-клик/⌘-клик «открыть в
	// новой вкладке», тогда как обычный клик идёт через SPA-навигацию handleOpen.
	const href = item.actionUrl ?? `/notifications/${item.id}`;
	const clickable = (
		<a
			href={href}
			className="block cursor-pointer no-underline outline-none"
			onClick={(e) => {
				// ⌘/Ctrl/Shift/средняя кнопка — отдаём браузеру (новая вкладка/окно).
				if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
				e.preventDefault();
				handleOpen();
			}}
		>
			{inner}
		</a>
	);

	// Mobile: нативный свайп по строке. Свайп влево → удалить (destructive),
	// свайп вправо → отметить прочитанным. Desktop сохраняет dropdown-меню.
	if (IS_MOBILE_PLATFORM && (onMarkRead ?? onDelete)) {
		const leftActions =
			!item.read && onMarkRead
				? [
						{
							key: "mark-read",
							label: t("markRead"),
							icon: <IconCheck />,
							tone: "primary" as const,
							onAction: () => onMarkRead(item.id),
						},
					]
				: [];
		const rightActions = onDelete
			? [
					{
						key: "delete",
						label: t("delete"),
						icon: <IconTrash />,
						tone: "destructive" as const,
						onAction: () => onDelete(item.id),
					},
				]
			: [];
		return (
			<SwipeActions
				className="rounded-[var(--radius-card)]"
				leftActions={leftActions}
				rightActions={rightActions}
			>
				{clickable}
			</SwipeActions>
		);
	}

	return clickable;
}
